import { externalEvents } from "@closerplatform/closer-sdk";
import { WsEventListener } from "@fidget/common/services/eventsListener";
import { IRoomStore } from "./RoomStore";
import { IRoom } from "./Room";
import { isConversationStatusClosed } from "./utils";
import { ArtichokePresence, ConversationStatus } from "@closerplatform/spinner-openapi";
import { logger } from "@fidget/common/utils";

export class RoomStoreEventListener implements WsEventListener {
  constructor(private _roomStoreFinder: () => IRoomStore, public name: string) {}

  get roomStore(): IRoomStore {
    return this._roomStoreFinder();
  }

  guestProfileUpdated(event: externalEvents.GuestProfileUpdated) {
    const found = [...this.roomStore.rooms].find(([, room]) => room.owner.id === event.customerId);
    if (found) {
      const room = found[1];
      room.owner.updateFromEvent(event);
    }
  }

  presenceUpdated(event: externalEvents.PresenceUpdated) {
    const roomByGuest = [...this.roomStore.rooms].find(
      ([, room]) => room.owner.id === event.userId
    );
    if (roomByGuest) {
      const newPresenceStatus = event.presence.valueOf() as ArtichokePresence;
      roomByGuest[1].owner.setStatus(newPresenceStatus);

      if (newPresenceStatus === ArtichokePresence.Unavailable) {
        roomByGuest[1].clearTypingUsers();
      }
    }
  }

  followerAdded(event: externalEvents.FollowerAdded) {
    this.roomStore.find(event.roomId, (room: IRoom) => room.addFollower(event.adviserId));
  }

  followerRemoved(event: externalEvents.FollowerRemoved) {
    this.roomStore.find(event.roomId, (room: IRoom) => room.removeFollower(event.adviserId));
  }

  allFollowersRemoved(event: externalEvents.AllFollowersRemoved) {
    this.roomStore.find(event.roomId, (room: IRoom) => room.removeFollowers());
  }

  assigneeChanged(event: externalEvents.AssigneeChanged) {
    this.roomStore.find(event.roomId, (room: IRoom) => {
      if (room.isAssignedToMe && this.roomStore.currentRoom?.id === room.id) {
        this.roomStore.resetCurrentRoom();
      }
      room.setAssignee(event.adviserId);
    });
  }

  assigneeRemoved(event: externalEvents.AssigneeRemoved) {
    this.roomStore.find(event.roomId, (room: IRoom) => room.setAssignee(undefined));
    if (
      this.roomStore.currentUserProfile?.id === event.adviserId &&
      this.roomStore.currentRoom?.id === event.roomId
    ) {
      this.roomStore.resetCurrentRoom();
    }
  }

  unreadCountUpdated(event: externalEvents.UnreadCountUpdated) {
    this.roomStore.find(event.roomId, (room: IRoom) => room.setUnreadCount(event.unreadCount));
  }

  conversationStatusChanged(event: externalEvents.ConversationStatusChanged) {
    logger.info(`Room conversationStatusChanged: ${event.roomId} status=${event.status}`);
    this.roomStore.find(event.roomId, (room: IRoom) => {
      logger.info(`Room conversationStatusChanged room found: ${event.roomId} status=${event.status}`);
      room.setStatus(event.status as ConversationStatus);

      if (
        room.isAssignedToMe &&
        this.roomStore.currentUserStore.orgConfig?.resetRoomViewOnClose &&
        this.roomStore.currentRoom?.id === event.roomId &&
        isConversationStatusClosed(event.status)
      ) {
        this.roomStore.resetCurrentRoom();
      }
    });
  }

  conversationSnoozed(event: externalEvents.ConversationSnoozed) {
    this.roomStore.find(event.roomId, (room: IRoom) => room.snoozeConversation());
  }

  conversationUnsnoozed(event: externalEvents.ConversationUnsnoozed) {
    this.roomStore.find(event.roomId, (room: IRoom) => room.unsnoozeConversation());
  }

  meetingScheduled(event: externalEvents.MeetingScheduled) {
    this.roomStore.find(event.roomId, (room: IRoom) =>
      room.addMeeting({ ...event, id: event.meetingId, agent: event.adviserId })
    );
  }

  meetingRescheduled(event: externalEvents.MeetingRescheduled) {
    this.roomStore.find(event.roomId, (room: IRoom) =>
      room.rescheduleMeeting({ ...event, id: event.meetingId })
    );
  }

  meetingCancelled(event: externalEvents.MeetingCancelled) {
    this.roomStore.find(event.roomId, (room: IRoom) => room.removeMeeting(event.meetingId));
  }

  connected() {
    this.roomStore.setup();
  }
}
